import { useState } from "react"
import { Check, Copy } from "lucide-react"

import { Button } from "@/components/ui/button"

/**
 * Copy-to-clipboard button with a two-second "Copied" state.
 *
 * One implementation rather than the four that had grown up separately, because
 * they had already drifted: two of them lacked the execCommand fallback below,
 * so copying silently did nothing on a page served over plain http — which is
 * every local dev session, and any coach on a host without TLS.
 *
 * `onCopied` is optional and fires once after a successful copy on either path.
 */
export function CopyButton({ text, label = "Copy", size = "sm", variant = "kf-outline", onCopied }) {
  const [copied, setCopied] = useState(false)

  const copy = async (event) => {
    // These buttons sit inside clickable table rows and dialogs; without this a
    // copy also triggers whatever the row does.
    event?.stopPropagation()

    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // navigator.clipboard is undefined outside a secure context.
      const textarea = document.createElement("textarea")
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }

    // Outside the try on purpose, so BOTH copy paths fire it. The execCommand
    // fallback is every local dev session and any coach on a host without TLS;
    // a copy that works but does not mark sent is the same silent-drift bug
    // this callback exists to remove.
    onCopied?.()

    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button type="button" variant={variant} size={size} onClick={copy}>
      {copied ? (
        <>
          <Check className="mr-1 h-3.5 w-3.5" /> Copied
        </>
      ) : (
        <>
          <Copy className="mr-1 h-3.5 w-3.5" /> {label}
        </>
      )}
    </Button>
  )
}
